import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ButtonColor, ButtonSize, ButtonTextPosition, ButtonCustomIcon, ButtonIconPosition } from '@interfaces/button';
import { StatusBarService } from '@services/status-bar/status-bar.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
})
export class ButtonComponent implements OnInit, OnDestroy {
  @Input() text = '';
  @Input() color: ButtonColor;
  @Input() size: ButtonSize;
  @Input() disabled = false;
  @Input() loading = false;
  @Input() textPosition: ButtonTextPosition;
  @Input() icon: ButtonCustomIcon;
  @Input() iconPosition: ButtonIconPosition = ButtonIconPosition.LEFT;
  slot: string;
  isDarkMode: boolean;
  darkModeSub: Subscription;
  customIconsData: { [key in ButtonCustomIcon]: string } = {
    [ButtonCustomIcon.TRANSFER]: 'transfer',
    [ButtonCustomIcon.DISTRIBUTE]: 'distribute',
    [ButtonCustomIcon.SEND]: 'send',
    [ButtonCustomIcon.TREND_UP]: 'trend-up',
    [ButtonCustomIcon.SEND_STOCK]: 'send-stock',
  };
  @ViewChild('ripple', { static: false }) ripple: ElementRef<HTMLSpanElement>;
  private readonly slotConfiguration = {
    [ButtonTextPosition.OUT]: 'icon-only',
    [ButtonIconPosition.LEFT]: 'start',
    [ButtonIconPosition.RIGHT]: 'end',
  } as const;

  constructor(private statusBarService: StatusBarService) { }

  ngOnInit() {
    this.slot = this.determineSlot();
    this.darkModeSub = this.statusBarService.currentDarkModeSource.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });
  }

  ngOnDestroy() {
    this.darkModeSub.unsubscribe();
  }

  isCustomIcon() {
    return !!this.customIconsData[this.icon];
  }

  createRipple(event: MouseEvent | TouchEvent): void {
    const button = (event.currentTarget as HTMLElement);
    const ripple = button.querySelector('.ripple') as HTMLSpanElement;
    if (!ripple) {
      return;
    }
    ripple.classList.remove('show');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    let x = 0;
    let y = 0;
    if (event instanceof MouseEvent) {
      x = event.clientX - rect.left - size / 2;
      y = event.clientY - rect.top - size / 2;
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      x = event.touches[0].clientX - rect.left - size / 2;
      y = event.touches[0].clientY - rect.top - size / 2;
    }
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add('show');
    setTimeout(() => {
      ripple.classList.remove('show');
    }, 400);
  }

  private determineSlot(): string {
    if (this.textPosition === ButtonTextPosition.OUT) {
      return this.slotConfiguration[ButtonTextPosition.OUT];
    }
    return this.slotConfiguration[this.iconPosition];
  }
}
